import { useId, useRef, useState } from 'react'
import { Check, Loader2 } from 'lucide-react'
import { Button } from '@repo/ui'
import type {
  ProposalSection,
  ProposalSectionMessage,
  deselectSectionVersion,
  getSectionMessages,
  selectSectionVersion
} from '@/api/proposals/proposals'
import type {
  PresentationSlide,
  PresentationSlideMessage,
  PresentationSlideVersion,
  deselectPresentationSlideVersion,
  getPresentationSlideMessages,
  selectPresentationSlideVersion
} from '@/api/presentations/presentations'
import { failureLine } from '@/common/errorMessages'
import type { ReactNode } from 'react'

// Version disclosures for the canvas artifacts. Both are self-contained and take their API
// clients as props, so they compile against today's modules and Task 4 mounts them inside the
// panels with the panel's own services. The parent owns reloading: a successful action awaits
// onChanged() before the busy state clears, and the fresh artifact arrives as new props.

// A disclosure shows at most this many rows; earlier ones wait behind an explicit reveal.
const visibleRowCount = 8

type VersionRowAction = {
  label: string
  disabled?: boolean
  // A disabled control must carry its reason beside it, reachable through aria-describedby.
  reason?: { id: string; text: string }
  run: () => Promise<unknown>
}

type VersionRow = {
  key: string
  // Chronological numbering is independent of the newest-first display order.
  name: string
  latest?: boolean
  // The row's own content line, e.g. a slide version's title.
  title?: string
  detail?: string
  selected?: boolean
  action: VersionRowAction
}

function proposalVersions(messages: Array<ProposalSectionMessage>): Array<ProposalSectionMessage> {
  return messages
    .filter((message) => message.author === 'assistant')
    .sort((left, right) => right.createdOnTsMs - left.createdOnTsMs || right.messageId - left.messageId)
}

function presentationVersions(messages: Array<PresentationSlideMessage>): Array<PresentationSlideVersion> {
  return messages
    .flatMap((message) => (message.version === null ? [] : [message.version]))
    .sort((left, right) => right.createdOnTsMs - left.createdOnTsMs || right.versionId - left.versionId)
}

function AcceptanceControl({
  kind,
  name,
  accepted,
  disabledReason,
  accept,
  returnToReview,
  onChanged,
  compact = false
}: {
  kind: 'section' | 'slide'
  name: string
  accepted: boolean
  disabledReason?: string
  accept: () => Promise<unknown>
  returnToReview: () => Promise<unknown>
  onChanged: () => void | Promise<void>
  compact?: boolean
}): ReactNode {
  const reasonId = useId()
  const [pending, setPending] = useState(false)
  const [failure, setFailure] = useState<string>()
  const inFlight = useRef(false)
  const blocked = !accepted && disabledReason !== undefined

  const run = () => {
    if (inFlight.current || blocked) return
    inFlight.current = true
    setPending(true)
    setFailure(undefined)
    void (async () => {
      try {
        await (accepted ? returnToReview() : accept())
        await onChanged()
      } catch (error) {
        setFailure(failureLine(error, `The ${kind} review state could not be updated.`))
      } finally {
        inFlight.current = false
        setPending(false)
      }
    })()
  }

  const accessibleName = accepted ? `Return ${kind} “${name}” to review` : `Accept ${kind} “${name}” for export`
  if (compact) {
    return (
      <div className="shrink-0">
        {blocked && (
          <span id={reasonId} className="sr-only">
            {disabledReason}
          </span>
        )}
        <Button
          type="button"
          size="sm"
          variant="outline"
          className={accepted ? 'text-primary' : undefined}
          aria-label={accessibleName}
          aria-pressed={accepted}
          aria-describedby={blocked ? reasonId : undefined}
          disabled={pending || blocked}
          onClick={run}
        >
          {pending ? (
            <Loader2 className="size-4 animate-spin motion-reduce:animate-none" aria-hidden="true" />
          ) : (
            <Check className="size-4" aria-hidden="true" />
          )}
          {pending ? (accepted ? 'Returning...' : 'Accepting...') : accepted ? 'Return to review' : 'Accept'}
        </Button>
        {failure !== undefined && (
          <p role="alert" className="mt-1.5 max-w-48 text-right text-xs text-destructive">
            {failure}
          </p>
        )}
      </div>
    )
  }
  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p
          id={blocked ? reasonId : undefined}
          className={
            accepted
              ? 'inline-flex items-center gap-1.5 text-xs font-medium text-primary'
              : 'text-xs text-muted-foreground'
          }
        >
          {accepted && <Check className="size-3.5" aria-hidden="true" />}
          {accepted
            ? 'Accepted for export'
            : disabledReason !== undefined
              ? disabledReason
              : `Accept this ${kind} to include it in the export.`}
        </p>
        <Button
          type="button"
          size="sm"
          variant={accepted ? 'outline' : 'default'}
          aria-label={accessibleName}
          aria-pressed={accepted}
          aria-describedby={blocked ? reasonId : undefined}
          disabled={pending || blocked}
          onClick={run}
        >
          {pending ? (
            <Loader2 className="size-4 animate-spin motion-reduce:animate-none" aria-hidden="true" />
          ) : !accepted ? (
            <Check className="size-4" aria-hidden="true" />
          ) : null}
          {pending ? (accepted ? 'Returning...' : 'Accepting...') : accepted ? 'Return to review' : `Accept ${kind}`}
        </Button>
      </div>
      {failure !== undefined && (
        <p role="alert" className="mt-2 text-xs text-destructive">
          {failure}
        </p>
      )}
    </div>
  )
}

export function PresentationSlideAcceptance({
  projectId,
  presentationId,
  slide,
  getMessages,
  select,
  deselect,
  describeIssue,
  onChanged
}: {
  projectId: number
  presentationId: number
  slide: PresentationSlide
  getMessages: typeof getPresentationSlideMessages
  select: typeof selectPresentationSlideVersion
  deselect: typeof deselectPresentationSlideVersion
  describeIssue: (issue: string) => string
  onChanged: () => void
}): ReactNode {
  const accepted = slide.selectedVersionId !== null
  const directFitReason =
    !accepted && slide.fit.status === 'overflow'
      ? slide.fit.issues[0] !== undefined
        ? describeIssue(slide.fit.issues[0])
        : 'This slide does not fit the slide layout.'
      : undefined
  return (
    <AcceptanceControl
      compact
      kind="slide"
      name={slide.title}
      accepted={accepted}
      {...(directFitReason === undefined ? {} : { disabledReason: directFitReason })}
      accept={async () => {
        const latest = presentationVersions(await getMessages(projectId, presentationId, slide.slideId))[0]
        if (latest === undefined) throw new Error('No saved slide version is available to accept.')
        return select(projectId, presentationId, slide.slideId, latest.versionId)
      }}
      returnToReview={() => deselect(projectId, presentationId, slide.slideId)}
      onChanged={onChanged}
    />
  )
}

export function ProposalSectionAcceptance({
  projectId,
  proposalId,
  section,
  getMessages,
  select,
  deselect,
  onChanged
}: {
  projectId: number
  proposalId: number
  section: ProposalSection
  getMessages: typeof getSectionMessages
  select: typeof selectSectionVersion
  deselect: typeof deselectSectionVersion
  onChanged: () => void
}): ReactNode {
  const accepted = section.selectedMessageId !== undefined
  return (
    <AcceptanceControl
      compact
      kind="section"
      name={section.heading}
      accepted={accepted}
      accept={async () => {
        const latest = proposalVersions(await getMessages(projectId, proposalId, section.sectionId))[0]
        if (latest === undefined) throw new Error('No saved section version is available to accept.')
        return select(projectId, proposalId, section.sectionId, latest.messageId)
      }}
      returnToReview={() => deselect(projectId, proposalId, section.sectionId)}
      onChanged={onChanged}
    />
  )
}

// Same convention SlideSpecification uses for its fit badge.
function fitLabel(status: PresentationSlideVersion['fit']['status']): string {
  return status.charAt(0).toUpperCase() + status.slice(1)
}

function VersionsRow({
  row,
  busy,
  onRun
}: {
  row: VersionRow
  busy: boolean
  onRun: (key: string, action: () => Promise<unknown>) => void
}): ReactNode {
  const disabled = busy || row.action.disabled === true
  return (
    <div role="listitem" className="py-2 text-sm">
      <div className="flex items-baseline justify-between gap-3">
        <p className="min-w-0 font-medium break-words">
          {row.name}
          {row.latest === true && <span className="ml-2 text-xs font-normal text-muted-foreground">Latest</span>}
          {row.detail !== undefined && (
            <span className="ml-2 text-xs font-normal text-muted-foreground">{row.detail}</span>
          )}
        </p>
        {row.selected === true && (
          <span className="shrink-0 text-xs font-medium text-primary">Selected for export</span>
        )}
      </div>
      {row.title !== undefined && <p className="mt-0.5 text-xs break-words text-muted-foreground">{row.title}</p>}
      <div className="mt-2 flex items-center gap-2">
        <Button
          type="button"
          size="sm"
          variant="outline"
          disabled={disabled}
          {...(row.action.reason === undefined ? {} : { 'aria-describedby': row.action.reason.id })}
          aria-label={`${row.name}, ${row.action.label}`}
          onClick={() => onRun(row.key, row.action.run)}
        >
          {busy && <Loader2 className="size-4 animate-spin motion-reduce:animate-none" aria-hidden="true" />}
          {row.action.label}
        </Button>
        {row.action.reason !== undefined && (
          <p id={row.action.reason.id} className="text-xs text-muted-foreground">
            {row.action.reason.text}
          </p>
        )}
      </div>
    </div>
  )
}

function VersionsDisclosure<T>({
  read,
  deriveRows,
  onChanged,
  readFallback,
  actionFallback
}: {
  read: () => Promise<Array<T>>
  deriveRows: (messages: Array<T>) => Array<VersionRow>
  onChanged: () => void
  readFallback: string
  actionFallback: string
}): ReactNode {
  const [expanded, setExpanded] = useState(false)
  const [messages, setMessages] = useState<Array<T>>([])
  const [readPending, setReadPending] = useState(false)
  const [actionPending, setActionPending] = useState(false)
  const [failure, setFailure] = useState<string>()
  const [revealed, setRevealed] = useState(false)
  // Like useCanvasExport's flightKeys: a ref rather than rendered state, so two clicks landing
  // in one batch cannot both start.
  const actionKeys = useRef(new Set<string>())
  // One token per expand. A read that outlives its expand must never write over the rows of
  // the expand now on screen.
  const readToken = useRef(0)

  const toggle = () => {
    if (expanded) {
      readToken.current += 1
      setExpanded(false)
      setMessages([])
      setRevealed(false)
      setReadPending(false)
      setFailure(undefined)
      return
    }
    setExpanded(true)
    setRevealed(false)
    setFailure(undefined)
    setReadPending(true)
    const token = readToken.current + 1
    readToken.current = token
    void (async () => {
      try {
        const next = await read()
        if (readToken.current !== token) return
        setMessages(next)
      } catch (error) {
        if (readToken.current !== token) return
        setMessages([])
        setFailure(failureLine(error, readFallback))
      } finally {
        if (readToken.current === token) setReadPending(false)
      }
    })()
  }

  const runAction = (key: string, action: () => Promise<unknown>) => {
    if (actionKeys.current.has(key)) return
    actionKeys.current.add(key)
    setActionPending(true)
    setFailure(undefined)
    void (async () => {
      try {
        await action()
        await onChanged()
      } catch (error) {
        setFailure(failureLine(error, actionFallback))
      } finally {
        actionKeys.current.delete(key)
        setActionPending(false)
      }
    })()
  }

  const rows = expanded && !readPending ? deriveRows(messages) : []
  const visibleRows = revealed ? rows : rows.slice(0, visibleRowCount)

  return (
    <div className="mt-3">
      <button
        type="button"
        className="text-xs font-medium text-foreground hover:underline"
        aria-expanded={expanded}
        onClick={toggle}
      >
        Versions
      </button>
      {expanded && (
        <div className="mt-2 space-y-2">
          {readPending && (
            <p role="status" className="flex items-center gap-1.5 text-xs text-muted-foreground">
              <Loader2 className="size-3 animate-spin motion-reduce:animate-none" aria-hidden="true" />
              Loading versions...
            </p>
          )}
          {!readPending && failure !== undefined && (
            <p role="alert" className="text-xs text-destructive">
              {failure}
            </p>
          )}
          {!readPending && failure === undefined && rows.length === 0 && (
            <p className="text-xs text-muted-foreground">No versions yet.</p>
          )}
          {rows.length > 0 && (
            <div role="list" className="divide-y rounded-md bg-muted/25 px-3">
              {visibleRows.map((row) => (
                <VersionsRow key={row.key} row={row} busy={actionPending} onRun={runAction} />
              ))}
            </div>
          )}
          {!revealed && rows.length > visibleRowCount && (
            <Button type="button" variant="ghost" size="sm" onClick={() => setRevealed(true)}>
              Show earlier versions
            </Button>
          )}
        </div>
      )}
    </div>
  )
}

export function ProposalSectionVersions({
  projectId,
  proposalId,
  section,
  getMessages,
  select,
  deselect,
  onChanged
}: {
  projectId: number
  proposalId: number
  section: ProposalSection
  getMessages: typeof getSectionMessages
  select: typeof selectSectionVersion
  deselect: typeof deselectSectionVersion
  onChanged: () => void
}): ReactNode {
  return (
    <div className="mt-4 border-t pt-3">
      <VersionsDisclosure<ProposalSectionMessage>
        read={() => getMessages(projectId, proposalId, section.sectionId)}
        deriveRows={(messages) =>
          proposalVersions(messages).map((message, position, versions) => {
            const selected = section.selectedMessageId === message.messageId
            return {
              key: `proposal-message-${message.messageId}`,
              name: `Draft ${versions.length - position}`,
              latest: position === 0,
              selected,
              action: {
                label: selected ? 'Return to review' : 'Use for export',
                run: () =>
                  selected
                    ? deselect(projectId, proposalId, section.sectionId)
                    : select(projectId, proposalId, section.sectionId, message.messageId)
              }
            }
          })
        }
        readFallback="The versions could not be loaded."
        actionFallback="The version selection did not finish."
        onChanged={onChanged}
      />
    </div>
  )
}

export function PresentationSlideVersions({
  projectId,
  presentationId,
  slide,
  getMessages,
  select,
  deselect,
  describeIssue,
  onChanged
}: {
  projectId: number
  presentationId: number
  slide: PresentationSlide
  getMessages: typeof getPresentationSlideMessages
  select: typeof selectPresentationSlideVersion
  deselect: typeof deselectPresentationSlideVersion
  describeIssue: (issue: string) => string
  onChanged: () => void
}): ReactNode {
  const reasonBase = useId()
  return (
    <div className="mt-4 border-t">
      <VersionsDisclosure<PresentationSlideMessage>
        read={() => getMessages(projectId, presentationId, slide.slideId)}
        deriveRows={(messages) =>
          presentationVersions(messages).map((version, position, versions) => {
            const selected = slide.selectedVersionId === version.versionId
            // The server rejects overflow selections; a disabled control mirrors that authority.
            // Deselect stays reachable even here, so only the unselected overflow blocks.
            const blocked = !selected && version.fit.status === 'overflow'
            return {
              key: `presentation-version-${version.versionId}`,
              name: `Version ${versions.length - position}`,
              latest: position === 0,
              title: version.title,
              detail: fitLabel(version.fit.status),
              selected,
              action: {
                label: selected ? 'Return to review' : 'Use this version for export',
                disabled: blocked,
                ...(blocked
                  ? {
                      reason: {
                        id: `${reasonBase}-reason-${version.versionId}`,
                        // An overflow with no reported issue still may not disable in silence.
                        text:
                          version.fit.issues[0] !== undefined
                            ? describeIssue(version.fit.issues[0])
                            : 'This version does not fit the slide layout.'
                      }
                    }
                  : {}),
                run: () =>
                  selected
                    ? deselect(projectId, presentationId, slide.slideId)
                    : select(projectId, presentationId, slide.slideId, version.versionId)
              }
            }
          })
        }
        readFallback="The versions could not be loaded."
        actionFallback="The version selection did not finish."
        onChanged={onChanged}
      />
    </div>
  )
}
